/**
 * Creating a unit's scope, and editing a draft.
 *
 * One form for both, because the fields are the same; what differs is that
 * a unit code is set once (the backend's update schema has no unitCode, and
 * a code that changed under a surveyor's bookmark would be worse than one
 * that cannot). Only a draft can be edited — anything else says so and
 * points back, rather than showing a form whose Save will be refused.
 *
 * Every field is bounded to the server's limit, with the count shown as it
 * fills. The two lists (populations, services) are typed one entry at a
 * time; see shared/ListInput.
 */

import { useId, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { useTaxonomy } from '../admin/useTaxonomy';
import { isCallFailure } from '../../lib/callable';
import {
  SCOPE_BEDS_MAX,
  SCOPE_HOURS_MAX,
  SCOPE_INDICATORS_MAX,
  SCOPE_LEADER_MAX,
  SCOPE_LIST_ITEM_MAX,
  SCOPE_LOCATION_MAX,
  SCOPE_POPULATIONS_MAX,
  SCOPE_SERVICES_MAX,
  SCOPE_TEXT_MAX,
  SCOPE_UNIT_CODE_MAX,
  SCOPE_UNIT_NAME_MAX,
} from '../../types/fieldLimits';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import { BackLink } from '../../shared/BackLink';
import { CharCount } from '../../shared/CharCount';
import { ListInput } from '../../shared/ListInput';
import { useCreateScope, useScopeDetail, useUpdateScope } from './useScopes';
import { AGE_GROUP_LABEL, AGE_GROUPS, EMPTY_FIELDS, toDateInput, type ScopeDetail, type ScopeFields } from './types';
import styles from '../../shared/Page.module.css';
import form from '../../shared/Form.module.css';
import own from './ScopeOfService.module.css';

export const NOT_DRAFT_TITLE = 'Only a draft can be edited';

function fieldsFrom(scope: ScopeDetail): ScopeFields {
  return {
    unitName: scope.unitName,
    unitCode: scope.unitCode,
    departmentId: scope.departmentId,
    description: scope.description ?? '',
    patientPopulations: scope.patientPopulations,
    servicesProvided: scope.servicesProvided,
    exclusionsLimitations: scope.exclusionsLimitations ?? '',
    hoursOfOperation: scope.hoursOfOperation ?? '',
    staffingRequirements: scope.staffingRequirements ?? '',
    equipmentRequirements: scope.equipmentRequirements ?? '',
    ageGroups: scope.ageGroups,
    bedCount: scope.bedCount === null ? '' : String(scope.bedCount),
    physicalLocation: scope.physicalLocation ?? '',
    unitLeader: scope.unitLeader ?? '',
    medicalDirector: scope.medicalDirector ?? '',
    qualityIndicators: scope.qualityIndicators,
    effectiveDate: toDateInput(scope.effectiveDate),
    nextReviewDate: toDateInput(scope.nextReviewDate),
  };
}

export function ScopeEditPage() {
  const { scopeId } = useParams();
  const { session } = useAuth();
  const orgId = session?.orgId ?? null;
  const editing = Boolean(scopeId);

  const detail = useScopeDetail(orgId, scopeId ?? null, editing);

  if (!editing) return <ScopeForm orgId={orgId} />;
  if (detail.isLoading) return <Spinner label="Opening the unit" />;
  if (detail.error || !detail.data) {
    return (
      <div className={styles.body}>
        <PageState title="Could not open the unit" action={{ to: '/scope-of-service', label: 'Back to scope of service' }} tone="alert">
          {isCallFailure(detail.error) ? detail.error.message : 'Something went wrong.'}
        </PageState>
      </div>
    );
  }
  if (detail.data.scope.status !== 'draft') {
    return (
      <div className={styles.body}>
        <PageState title={NOT_DRAFT_TITLE} action={{ to: `/scope-of-service/${scopeId}`, label: 'Back to the unit' }}>
          This scope is {detail.data.scope.status === 'in_review' ? 'in review' : detail.data.scope.status}. To change an
          approved scope, start a new version from its page; it opens as a draft.
        </PageState>
      </div>
    );
  }
  return <ScopeForm orgId={orgId} scope={detail.data.scope} />;
}

function ScopeForm({ orgId, scope }: { orgId: string | null; scope?: ScopeDetail }) {
  const navigate = useNavigate();
  const create = useCreateScope(orgId);
  const update = useUpdateScope(orgId, scope?.id ?? '');
  const departments = useTaxonomy('department', Boolean(orgId));
  const [f, setF] = useState<ScopeFields>(() => (scope ? fieldsFrom(scope) : EMPTY_FIELDS));
  const [touched, setTouched] = useState(false);
  const ids = useId();
  const id = (name: string) => `${ids}-${name}`;

  const set = <K extends keyof ScopeFields>(key: K, value: ScopeFields[K]) => setF((c) => ({ ...c, [key]: value }));
  const text = (key: keyof ScopeFields, max: number) => (e: { target: { value: string } }) =>
    set(key, e.target.value.slice(0, max) as never);

  const nameMissing = touched && !f.unitName.trim();
  const codeMissing = touched && !scope && !f.unitCode.trim();
  const pending = create.isPending || update.isPending;
  const error = create.error ?? update.error;
  const backTo = scope ? `/scope-of-service/${scope.id}` : '/scope-of-service';

  function submit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!f.unitName.trim() || (!scope && !f.unitCode.trim())) return;
    if (scope) {
      update.mutate(f, { onSuccess: () => navigate(backTo) });
    } else {
      create.mutate(f, { onSuccess: (newId) => navigate(`/scope-of-service/${newId}`) });
    }
  }

  return (
    <>
      <header className={styles.header}>
        <div className={styles.titled}>
          {scope && <p className={styles.eyebrow}>{scope.unitCode} · Version {scope.version} · Draft</p>}
          <div className={styles.titleLine}>
            <BackLink to={backTo} label={scope ? 'Back to the unit' : 'Back to scope of service'} />
            <h1 className={styles.title}>{scope ? `Edit ${scope.unitName}` : 'New unit scope'}</h1>
          </div>
          <p className={styles.subtitle}>
            {scope
              ? 'Changes are saved to this draft. Submit it for review when it is ready.'
              : 'Describe what one unit is authorized to do. It starts as a draft you can come back to.'}
          </p>
        </div>
      </header>

      <div className={styles.body}>
        <form className={form.form} onSubmit={submit} noValidate style={{ marginTop: 0, maxWidth: 720 }}>
          {error && (
            <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
              {isCallFailure(error) ? error.message : 'The unit could not be saved.'}
            </div>
          )}

          <div className={form.row}>
            <div className={form.field}>
              <label className={form.label} htmlFor={id('name')}>Unit name</label>
              <input id={id('name')} className={form.input} type="text" required
                maxLength={SCOPE_UNIT_NAME_MAX} value={f.unitName}
                onChange={text('unitName', SCOPE_UNIT_NAME_MAX)}
                aria-invalid={nameMissing || undefined}
                aria-describedby={nameMissing ? id('name-err') : id('name-hint')} autoComplete="off" />
              <CharCount value={f.unitName} max={SCOPE_UNIT_NAME_MAX} />
              {nameMissing
                ? <p className={form.error} id={id('name-err')}>Give the unit a name.</p>
                : <p className={form.hint} id={id('name-hint')}>As it appears on the org chart.</p>}
            </div>

            <div className={form.field}>
              <label className={form.label} htmlFor={id('code')}>Unit code</label>
              <input id={id('code')} className={form.input} type="text" required={!scope}
                maxLength={SCOPE_UNIT_CODE_MAX} value={f.unitCode}
                onChange={text('unitCode', SCOPE_UNIT_CODE_MAX)}
                readOnly={Boolean(scope)}
                aria-invalid={codeMissing || undefined}
                aria-describedby={codeMissing ? id('code-err') : id('code-hint')} autoComplete="off" />
              {!scope && <CharCount value={f.unitCode} max={SCOPE_UNIT_CODE_MAX} />}
              {codeMissing
                ? <p className={form.error} id={id('code-err')}>Give the unit a code.</p>
                : <p className={form.hint} id={id('code-hint')}>
                    {scope ? 'A code cannot change once the unit exists.' : 'Short and unique — it cannot change later.'}
                  </p>}
            </div>
          </div>

          <div className={form.field}>
            <label className={form.label} htmlFor={id('dept')}>
              Department <span className={form.optional}>(optional)</span>
            </label>
            <select id={id('dept')} className={form.select} value={f.departmentId ?? ''}
              onChange={(e) => set('departmentId', e.target.value || null)}>
              <option value="">No department</option>
              {(departments.data ?? []).filter((d) => d.isActive || d.id === f.departmentId).map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>

          <div className={form.field}>
            <label className={form.label} htmlFor={id('desc')}>
              Description <span className={form.optional}>(optional)</span>
            </label>
            <textarea id={id('desc')} className={form.textarea} rows={4}
              maxLength={SCOPE_TEXT_MAX} value={f.description} onChange={text('description', SCOPE_TEXT_MAX)}
              aria-describedby={id('desc-hint')} />
            <CharCount value={f.description} max={SCOPE_TEXT_MAX} />
            <p className={form.hint} id={id('desc-hint')}>The unit's purpose and setting, in a paragraph.</p>
          </div>

          <h2 className={form.group}>Clinical scope</h2>

          <div className={form.field}>
            <span className={form.label} id={id('pop-label')}>
              Patient populations <span className={form.optional}>(optional)</span>
            </span>
            <ListInput id={id('pop')} labelledBy={id('pop-label')} describedBy={id('pop-hint')}
              value={f.patientPopulations} onChange={(v) => set('patientPopulations', v)}
              max={SCOPE_POPULATIONS_MAX} itemMax={SCOPE_LIST_ITEM_MAX} />
            <p className={form.hint} id={id('pop-hint')}>Who the unit serves. Press Enter after each one.</p>
          </div>

          <fieldset className={form.field} style={{ border: 0, padding: 0 }} aria-describedby={id('age-hint')}>
            <legend className={form.label}>Age groups served <span className={form.optional}>(optional)</span></legend>
            <ul className={own.checks}>
              {AGE_GROUPS.map((g) => (
                <li key={g}>
                  <label className={own.check}>
                    <input type="checkbox" checked={f.ageGroups.includes(g)}
                      onChange={() => set('ageGroups', f.ageGroups.includes(g) ? f.ageGroups.filter((x) => x !== g) : AGE_GROUPS.filter((x) => x === g || f.ageGroups.includes(x)))} />
                    {AGE_GROUP_LABEL[g]}
                  </label>
                </li>
              ))}
            </ul>
            <p className={form.hint} id={id('age-hint')}>The bands a surveyor asks a unit to declare; age-specific competencies follow from them.</p>
          </fieldset>

          <div className={form.field}>
            <span className={form.label} id={id('svc-label')}>
              Services provided <span className={form.optional}>(optional)</span>
            </span>
            <ListInput id={id('svc')} labelledBy={id('svc-label')} describedBy={id('svc-hint')}
              value={f.servicesProvided} onChange={(v) => set('servicesProvided', v)}
              max={SCOPE_SERVICES_MAX} itemMax={SCOPE_LIST_ITEM_MAX} />
            <p className={form.hint} id={id('svc-hint')}>What the unit does. Press Enter after each one.</p>
          </div>

          <div className={form.field}>
            <label className={form.label} htmlFor={id('excl')}>
              Exclusions and limitations <span className={form.optional}>(optional)</span>
            </label>
            <textarea id={id('excl')} className={form.textarea} rows={3}
              maxLength={SCOPE_TEXT_MAX} value={f.exclusionsLimitations}
              onChange={text('exclusionsLimitations', SCOPE_TEXT_MAX)} aria-describedby={id('excl-hint')} />
            <CharCount value={f.exclusionsLimitations} max={SCOPE_TEXT_MAX} />
            <p className={form.hint} id={id('excl-hint')}>What the unit does not do, and where those patients go instead.</p>
          </div>

          <div className={form.field}>
            <label className={form.label} htmlFor={id('hours')}>
              Hours of operation <span className={form.optional}>(optional)</span>
            </label>
            <input id={id('hours')} className={form.input} type="text"
              maxLength={SCOPE_HOURS_MAX} value={f.hoursOfOperation}
              onChange={text('hoursOfOperation', SCOPE_HOURS_MAX)} />
            <CharCount value={f.hoursOfOperation} max={SCOPE_HOURS_MAX} />
          </div>

          <div className={form.field}>
            <label className={form.label} htmlFor={id('staff')}>
              Staffing requirements <span className={form.optional}>(optional)</span>
            </label>
            <textarea id={id('staff')} className={form.textarea} rows={3}
              maxLength={SCOPE_TEXT_MAX} value={f.staffingRequirements}
              onChange={text('staffingRequirements', SCOPE_TEXT_MAX)} aria-describedby={id('staff-hint')} />
            <CharCount value={f.staffingRequirements} max={SCOPE_TEXT_MAX} />
            <p className={form.hint} id={id('staff-hint')}>Roles, ratios, competencies and credentials the unit needs.</p>
          </div>

          <div className={form.field}>
            <label className={form.label} htmlFor={id('equip')}>
              Equipment and technology <span className={form.optional}>(optional)</span>
            </label>
            <textarea id={id('equip')} className={form.textarea} rows={3}
              maxLength={SCOPE_TEXT_MAX} value={f.equipmentRequirements}
              onChange={text('equipmentRequirements', SCOPE_TEXT_MAX)} />
            <CharCount value={f.equipmentRequirements} max={SCOPE_TEXT_MAX} />
          </div>

          <div className={form.field}>
            <span className={form.label} id={id('qi-label')}>
              Quality indicators <span className={form.optional}>(optional)</span>
            </span>
            <ListInput id={id('qi')} labelledBy={id('qi-label')} describedBy={id('qi-hint')}
              value={f.qualityIndicators} onChange={(v) => set('qualityIndicators', v)}
              max={SCOPE_INDICATORS_MAX} itemMax={SCOPE_LIST_ITEM_MAX} />
            <p className={form.hint} id={id('qi-hint')}>What the unit measures to show it does what this document says. Press Enter after each one.</p>
          </div>

          <h2 className={form.group}>Unit profile</h2>

          <div className={form.row}>
            <div className={form.field}>
              <label className={form.label} htmlFor={id('loc')}>
                Physical location <span className={form.optional}>(optional)</span>
              </label>
              <input id={id('loc')} className={form.input} type="text" maxLength={SCOPE_LOCATION_MAX}
                value={f.physicalLocation} onChange={text('physicalLocation', SCOPE_LOCATION_MAX)} autoComplete="off" />
              <CharCount value={f.physicalLocation} max={SCOPE_LOCATION_MAX} />
            </div>
            <div className={form.field}>
              <label className={form.label} htmlFor={id('beds')}>
                Beds <span className={form.optional}>(optional)</span>
              </label>
              <input id={id('beds')} className={form.input} type="number" min={0} max={SCOPE_BEDS_MAX} step={1}
                value={f.bedCount} onChange={(e) => set('bedCount', e.target.value)}
                aria-describedby={id('beds-hint')} style={{ maxWidth: 160 }} />
              <p className={form.hint} id={id('beds-hint')}>Licensed or staffed beds, bays or chairs.</p>
            </div>
          </div>

          <div className={form.row}>
            <div className={form.field}>
              <label className={form.label} htmlFor={id('leader')}>
                Unit leader <span className={form.optional}>(optional)</span>
              </label>
              <input id={id('leader')} className={form.input} type="text" maxLength={SCOPE_LEADER_MAX}
                value={f.unitLeader} onChange={text('unitLeader', SCOPE_LEADER_MAX)} autoComplete="off" aria-describedby={id('leader-hint')} />
              <CharCount value={f.unitLeader} max={SCOPE_LEADER_MAX} />
              <p className={form.hint} id={id('leader-hint')}>The nurse manager or director accountable for the unit.</p>
            </div>
            <div className={form.field}>
              <label className={form.label} htmlFor={id('md')}>
                Medical director <span className={form.optional}>(optional)</span>
              </label>
              <input id={id('md')} className={form.input} type="text" maxLength={SCOPE_LEADER_MAX}
                value={f.medicalDirector} onChange={text('medicalDirector', SCOPE_LEADER_MAX)} autoComplete="off" />
              <CharCount value={f.medicalDirector} max={SCOPE_LEADER_MAX} />
            </div>
          </div>

          <h2 className={form.group}>Dates</h2>

          <div className={form.row}>
            <div className={form.field}>
              <label className={form.label} htmlFor={id('eff')}>
                Effective date <span className={form.optional}>(optional)</span>
              </label>
              <input id={id('eff')} className={form.input} type="date" value={f.effectiveDate}
                onChange={(e) => set('effectiveDate', e.target.value)} />
            </div>
            <div className={form.field}>
              <label className={form.label} htmlFor={id('rev')}>
                Next review <span className={form.optional}>(optional)</span>
              </label>
              <input id={id('rev')} className={form.input} type="date" value={f.nextReviewDate}
                onChange={(e) => set('nextReviewDate', e.target.value)} />
            </div>
          </div>

          <div className={styles.actions}>
            <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={pending}>
              {pending ? 'Saving…' : scope ? 'Save changes' : 'Create unit scope'}
            </button>
            <Link to={backTo} className={styles.btn}>Cancel</Link>
          </div>
        </form>
      </div>
    </>
  );
}
